/* Octilinear A* on an integer lattice: moves in 8 directions, pays for every
   turn (45 or 90 degrees; sharper turns are not allowed) so traces stay long
   and straight like printed circuit lines. Callers add costs to keep traces
   off each other and block lattice points near nodes and symbols. */

export interface LatticePoint {
  x: number;
  y: number;
}

/** Direction index d points along DIRS[d]; 0 = east, 2 = south (y grows down). */
export const DIRS: ReadonlyArray<LatticePoint> = [
  { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }, { x: -1, y: 1 },
  { x: -1, y: 0 }, { x: -1, y: -1 }, { x: 0, y: -1 }, { x: 1, y: -1 },
];

export interface RouteRequest {
  start: LatticePoint;
  /** Direction the trace is already travelling when it reaches `start`. */
  startDir: number;
  goal: LatticePoint;
  /** Direction the trace must be travelling when it leaves `goal`. */
  goalDir: number;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  isBlocked: (x: number, y: number) => boolean;
  /** Extra cost for stepping onto a point (e.g. crossing another trace). */
  pointCost?: (x: number, y: number) => number;
  /** Extra cost for using the segment a -> b (e.g. overlapping another trace). */
  edgeCost?: (ax: number, ay: number, bx: number, by: number) => number;
}

const TURN_COST = [0, 1.2, 3.5];
const SQRT2 = Math.SQRT2;
/* Two diagonals meeting at 90 degrees read as a sharp peak, unlike circuit traces:
   directly (a V) or across a flat shorter than MIN_RUN steps (a spike). */
const PEAK_COST = 10;
const MIN_RUN = 3;
const ROTS = 3; // last 45-degree bend: 0 none, 1 clockwise, 2 counter-clockwise
// Per direction: one settled state (a diagonal, or a straight run of MIN_RUN or more),
// plus a short straight run's length (1 to MIN_RUN - 1) times its last bend.
const VARIANTS = 1 + (MIN_RUN - 1) * ROTS;
const variantOf = (run: number, rot: number) => (run >= MIN_RUN ? 0 : 1 + (run - 1) * ROTS + rot);

export function turnSteps(a: number, b: number): number {
  const d = Math.abs(a - b) % 8;
  return Math.min(d, 8 - d);
}

/** Rotation of a 45-degree turn from a to b: 1 clockwise, 2 counter-clockwise, 0 otherwise. */
const rotation = (a: number, b: number) => {
  const d = (b - a + 8) % 8;
  return d === 1 ? 1 : d === 7 ? 2 : 0;
};

/** Extra cost of turning from d to nd after `run` straight steps, given the last 45-degree bend. */
function peakCost(d: number, nd: number, run: number, rot: number): number {
  const turn = turnSteps(d, nd);
  if (turn === 2 && d % 2 === 1) return PEAK_COST;
  if (turn === 1 && d % 2 === 0 && run < MIN_RUN && rot !== 0 && rotation(d, nd) === rot) return PEAK_COST;
  return 0;
}

/** Minimal binary heap of state indices ordered by priority, ties by insertion. */
class Heap {
  private items: Array<[number, number, number]> = [];
  private seq = 0;
  get size() { return this.items.length; }
  push(state: number, priority: number) {
    const a = this.items;
    a.push([priority, this.seq++, state]);
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (a[p][0] < a[i][0] || (a[p][0] === a[i][0] && a[p][1] < a[i][1])) break;
      [a[p], a[i]] = [a[i], a[p]];
      i = p;
    }
  }
  pop(): number {
    const a = this.items;
    const top = a[0];
    const end = a.pop()!;
    if (a.length > 0) {
      a[0] = end;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < a.length && (a[l][0] < a[m][0] || (a[l][0] === a[m][0] && a[l][1] < a[m][1]))) m = l;
        if (r < a.length && (a[r][0] < a[m][0] || (a[r][0] === a[m][0] && a[r][1] < a[m][1]))) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i], a[m]];
        i = m;
      }
    }
    return top[2];
  }
}

/** Returns the lattice points from start to goal (inclusive), or null. */
export function routeOctilinear(req: RouteRequest): LatticePoint[] | null {
  const { bounds, start, goal } = req;
  const width = bounds.maxX - bounds.minX + 1;
  const height = bounds.maxY - bounds.minY + 1;
  const inBounds = (x: number, y: number) => x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
  if (!inBounds(start.x, start.y) || !inBounds(goal.x, goal.y)) return null;

  const PER_CELL = 8 * VARIANTS;
  const stateOf = (x: number, y: number, d: number, run: number, rot: number) =>
    ((y - bounds.minY) * width + (x - bounds.minX)) * PER_CELL + d * VARIANTS + variantOf(run, rot);
  const total = width * height * PER_CELL;
  const g = new Float64Array(total).fill(Infinity);
  const parent = new Int32Array(total).fill(-1);
  const closed = new Uint8Array(total);
  // The callbacks are fixed for one route, so ask each once per point (and per step direction).
  const blockedAt = new Int8Array(width * height); // 0 unknown, 1 free, 2 blocked
  const pointAt = new Float64Array(width * height).fill(NaN);
  const edgeAt = new Float64Array(width * height * 8).fill(NaN);
  const heuristic = (x: number, y: number) => {
    const dx = Math.abs(x - goal.x);
    const dy = Math.abs(y - goal.y);
    return Math.max(dx, dy) + (SQRT2 - 1) * Math.min(dx, dy);
  };

  // The route arrives at `start` already travelling straight, so its first turn is free of the run rule.
  const s0 = stateOf(start.x, start.y, req.startDir, MIN_RUN, 0);
  g[s0] = 0;
  const heap = new Heap();
  heap.push(s0, heuristic(start.x, start.y));

  let best = -1;
  let bestCost = Infinity;
  while (heap.size > 0) {
    const s = heap.pop();
    if (closed[s]) continue;
    closed[s] = 1;
    const v = s % VARIANTS;
    const run = v === 0 ? MIN_RUN : 1 + Math.floor((v - 1) / ROTS);
    const rot = v === 0 ? 0 : (v - 1) % ROTS;
    const d = Math.floor(s / VARIANTS) % 8;
    const cell = Math.floor(s / PER_CELL);
    const x = (cell % width) + bounds.minX;
    const y = Math.floor(cell / width) + bounds.minY;
    if (g[s] >= bestCost) break;

    if (x === goal.x && y === goal.y) {
      const turn = turnSteps(d, req.goalDir);
      if (turn <= 2) {
        const cost = g[s] + TURN_COST[turn] + peakCost(d, req.goalDir, run, rot);
        if (cost < bestCost) { bestCost = cost; best = s; }
      }
      continue;
    }

    for (let nd = 0; nd < 8; nd++) {
      const turn = turnSteps(d, nd);
      if (turn > 2) continue;
      const nx = x + DIRS[nd].x;
      const ny = y + DIRS[nd].y;
      if (!inBounds(nx, ny)) continue;
      const isGoal = nx === goal.x && ny === goal.y;
      const ncell = (ny - bounds.minY) * width + (nx - bounds.minX);
      if (!isGoal) {
        if (blockedAt[ncell] === 0) blockedAt[ncell] = req.isBlocked(nx, ny) ? 2 : 1;
        if (blockedAt[ncell] === 2) continue;
      }
      if (Number.isNaN(pointAt[ncell])) pointAt[ncell] = req.pointCost?.(nx, ny) ?? 0;
      const edge = cell * 8 + nd;
      if (Number.isNaN(edgeAt[edge])) edgeAt[edge] = req.edgeCost?.(x, y, nx, ny) ?? 0;
      const step = nd % 2 === 0 ? 1 : SQRT2;
      const extra = pointAt[ncell] + edgeAt[edge];
      const nrun = turn === 0 ? Math.min(MIN_RUN, run + 1) : 1;
      const nrot = turn === 0 ? rot : turn === 1 ? rotation(d, nd) : 0;
      // Only a short straight run remembers its last bend; diagonals share the settled state.
      const ns = nd % 2 === 1 ? stateOf(nx, ny, nd, MIN_RUN, 0) : stateOf(nx, ny, nd, nrun, nrot);
      const cost = g[s] + step + TURN_COST[turn] + peakCost(d, nd, run, rot) + extra;
      if (cost < g[ns]) {
        g[ns] = cost;
        parent[ns] = s;
        heap.push(ns, cost + heuristic(nx, ny));
      }
    }
  }

  if (best < 0) return null;
  const path: LatticePoint[] = [];
  for (let s = best; s >= 0; s = parent[s]) {
    const cell = Math.floor(s / PER_CELL);
    path.push({ x: (cell % width) + bounds.minX, y: Math.floor(cell / width) + bounds.minY });
  }
  return path.reverse();
}
